import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useSlotGroup,
  useContentToken,
  useCollectionBinding,
  usePropertyAddressUrl,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import {
  condenseOfficeHours,
  formatDisplayPhone,
  type OfficeHourEntry,
} from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

// Address slot group — mirrors the Footer's address pattern so the address
// value becomes a link to the property's Google Place page when set. The
// schema literal references this constant (extractor follows one level).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// Placeholder office hours used when the property has no office_hours
// collection data, so the Hours block always renders (mirrors the address /
// phone fallbacks). The editor's real office-hours data overrides these.
const FALLBACK_OFFICE_HOURS: OfficeHourEntry[] = [
  { day: 'Mon', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Tue', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Wed', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Thu', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Fri', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Sat', open_time: null, close_time: null },
  { day: 'Sun', open_time: '9:00 am', close_time: '6:00 pm' },
];

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const formBlockPath = 'scheduletourform.form_block';

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-tour-scheduler',
  });

  const formHeading = useContentToken('scheduletourform.form_heading');
  const addressLabel = useContentToken('scheduletourform.address_label');
  const phoneLabel = useContentToken('scheduletourform.phone_label');
  const hoursLabel = useContentToken('scheduletourform.hours_label');

  const address = useSlotGroup('scheduletourform.address', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const addressHref = address.url || propertyAddressUrl || '';

  const phone = useContentToken('scheduletourform.phone') || '';
  const hasPhone = !!phone;
  const phoneDisplay = hasPhone ? formatDisplayPhone(phone) : '(555) 555-5555';

  const officeHours = useCollectionBinding<OfficeHourEntry>('scheduletourform.office_hours', {
    collection: 'office_hours',
  });
  const hoursSource = officeHours.length > 0 ? officeHours : FALLBACK_OFFICE_HOURS;
  const hoursRows = condenseOfficeHours(hoursSource);

  return (
    <>
      <section className="stf-section">
        <div className="stf-container">
          <div className="stf-grid">
            {/* ── Left column: contact info ── */}
            <div className="stf-info-col">
              <div className="stf-info-block">
                <EditableToken path="scheduletourform.address_label" as="h2" className="stf-info-heading">
                  {addressLabel || 'Address'}
                </EditableToken>
                <EditableSlotGroup groupPath="scheduletourform.address" definition={addressLinkDef}>
                  {addressHref ? (
                    <a
                      href={addressHref}
                      target={address._target || '_blank'}
                      rel="noopener noreferrer"
                      className="stf-info-value stf-info-link"
                    >
                      {address.text || '123 Main Street, City, ST 00000'}
                    </a>
                  ) : (
                    <p className="stf-info-value">
                      {address.text || '123 Main Street, City, ST 00000'}
                    </p>
                  )}
                </EditableSlotGroup>
              </div>

              <div className="stf-info-block">
                <EditableToken path="scheduletourform.phone_label" as="h2" className="stf-info-heading">
                  {phoneLabel || 'Phone'}
                </EditableToken>
                {hasPhone ? (
                  <a href={`tel:${phone}`} className="dniphone dniphonehref stf-info-value stf-info-link">
                    <EditableToken path="scheduletourform.phone" as="span">
                      {phoneDisplay}
                    </EditableToken>
                  </a>
                ) : (
                  <EditableToken path="scheduletourform.phone" as="p" className="stf-info-value">
                    {phoneDisplay}
                  </EditableToken>
                )}
              </div>

              {hoursRows.length > 0 && (
                <div className="stf-info-block">
                  <EditableToken path="scheduletourform.hours_label" as="h2" className="stf-info-heading">
                    {hoursLabel || 'Hours'}
                  </EditableToken>
                  <div className="stf-hours-grid">
                    {hoursRows.map((row, i) => (
                      <div key={i} className="stf-hours-row">
                        <span className="stf-hours-day">{row.dayLabel}</span>
                        <span className="stf-hours-time">{row.timeLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Right column: heading + tour scheduler block ── */}
            <div className="stf-form-col">
              <EditableToken path="scheduletourform.form_heading" as="h2" className="stf-form-heading">
                {formHeading || 'Schedule A Tour'}
              </EditableToken>
              <div className="stf-form-wrap">
                <EditableToken path="scheduletourform.form_block" as="div">
                  {formSlot ? (
                    <SectionContextProvider sectionId={formSlot.sectionId}>
                      <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                    </SectionContextProvider>
                  ) : (
                    <div className="stf-form-empty-card">
                      <p className="stf-form-empty">No form block selected</p>
                    </div>
                  )}
                </EditableToken>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .stf-section {
          padding: 3rem 1.5rem;
        }
        @media (min-width: 1024px) {
          .stf-section { padding: 5rem 2rem; }
        }
        .stf-container {
          max-width: 72rem;
          margin: 0 auto;
        }

        .stf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
        }
        @media (min-width: 900px) {
          .stf-grid {
            grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
            gap: 4rem;
            align-items: start;
          }
        }

        /* ── Left column: contact info ── */
        .stf-info-col {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }
        .stf-info-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 6vw, 2rem);
          line-height: 1.1;
          font-weight: 600;
          color: var(--vibe-color-primary);
          margin: 0 0 0.75rem;
        }
        .stf-info-value {
          font-family: var(--vibe-font-body);
          font-size: 1.0625rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .stf-info-link {
          text-decoration: none;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .stf-info-link:hover { color: var(--vibe-color-primary); }

        .stf-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .stf-hours-row {
          display: flex;
          gap: 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 1.0625rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
        }
        .stf-hours-day { min-width: 7rem; }
        .stf-hours-time { color: var(--vibe-color-text); }

        /* ── Right column: heading + form ── */
        .stf-form-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 8vw, 2.5rem);
          line-height: 1.1;
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0 0 1.75rem;
        }
        @media (min-width: 1024px) {
          .stf-form-heading { font-size: 3rem; }
        }
        .stf-form-wrap {
          width: 100%;
        }
        /* Uppercase the block's wizard heading */
        .stf-form-wrap h3 {
          text-transform: uppercase;
        }
        .stf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_heading': {
      label: 'Form Heading',
      type: 'text',
      defaultValue: 'Schedule A Tour',
    },
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduletourform.address_label': {
      label: 'Address Label',
      type: 'text',
      defaultValue: 'Address',
    },
    'scheduletourform.phone_label': {
      label: 'Phone Label',
      type: 'text',
      defaultValue: 'Phone',
    },
    'scheduletourform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'scheduletourform.hours_label': {
      label: 'Hours Label',
      type: 'text',
      defaultValue: 'Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': addressLinkDef,
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
